import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Card, CardContent, Button, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, Divider, Grid, useMediaQuery, useTheme, IconButton, Avatar, alpha
} from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { format, parseISO } from 'date-fns';
import { 
  Building2, Calendar, AlertTriangle, CheckCircle, Clock, 
  CreditCard, Plus, RefreshCw, DollarSign, HelpCircle,
  ArrowRight, Info, ChevronRight, Eye, Search
} from 'lucide-react';

// Import components
import Sidebar from '../Sidebar';
import BankAccountModal from './BankAccountModal';
import PayoutDetailsModal from './PayoutDetailsModal';
import NoPayoutsPlaceholder from './NoPayoutsPlaceholder';

// Import service
import { payoutService } from '../../../services/payoutService';

// Enhanced Payment Method Card Component
const PaymentMethodCard = ({ bankAccounts = [], defaultBankAccount, formatDate, handleOpenBankModal }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  return (
    <Card 
      sx={{ 
        height: '100%',
        borderRadius: { xs: 3, sm: 4 },
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.8)',
        bgcolor: 'white'
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ p: { xs: 3, sm: 4 }, pb: { xs: 2, sm: 3 } }}>
          {/* Header with icon and title */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: { xs: 3, sm: 4 },
            gap: 1.5
          }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: 'rgba(0, 180, 216, 0.1)',
                mr: 1.5,
                flexShrink: 0
              }}
            >
              <Building2 size={20} style={{ color: '#00B4D8' }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.125rem', sm: '1.25rem' },
                color: '#111827'
              }}
            >
              Payment Method
            </Typography>
          </Box>

          <Typography 
            variant="subtitle2" 
            sx={{ 
              mb: 2.5, 
              fontWeight: 600, 
              color: '#4B5563',
              fontSize: { xs: '0.875rem', sm: '0.9375rem' }
            }}
          >
            Your linked bank accounts
          </Typography>

          {bankAccounts.length > 0 ? (
            <Box sx={{ mb: { xs: 3, sm: 4 } }}>
              {/* Only show the default account in the card view */}
              {defaultBankAccount && (
                <Box
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    mb: 2,
                    border: '1px solid',
                    borderColor: 'rgba(0, 180, 216, 0.3)',
                    borderRadius: 2,
                    bgcolor: 'rgba(0, 180, 216, 0.05)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                      borderColor: 'rgba(0, 180, 216, 0.5)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <CreditCard size={20} style={{ color: '#00B4D8', marginRight: 12, marginTop: 4 }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        flexWrap: { xs: 'wrap', md: 'nowrap' },
                        gap: { xs: 1, md: 0 }
                      }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            color: '#111827',
                            fontSize: { xs: '0.9375rem', sm: '1rem' },
                            marginRight: { md: 2 }
                          }}
                        >
                          {defaultBankAccount.bank_name} •••• {defaultBankAccount.last4}
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1, 
                          ml: { xs: 0, md: 'auto' },
                          mt: { xs: 0.5, md: 0 }
                        }}>
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 10,
                              bgcolor: 'rgba(0, 180, 216, 0.1)',
                              color: '#00B4D8',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Default
                          </Box>
                          <Box
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 10,
                              bgcolor: 'rgba(76, 175, 80, 0.1)',
                              color: '#4CAF50',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Linked
                          </Box>
                        </Box>
                      </Box>
                      <Typography 
                        variant="caption" 
                        color="textSecondary"
                        sx={{
                          fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                          mt: 0.5,
                          display: 'block'
                        }}
                      >
                        Added on {formatDate(defaultBankAccount.created_at)} • {defaultBankAccount.account_type || 'checking'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ 
                  mb: 3,
                  fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                  lineHeight: 1.5
                }}
              >
                You can manage your linked accounts and set your default payment method.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ 
              textAlign: 'center', 
              py: { xs: 3, sm: 4 }, 
              mb: { xs: 2, sm: 3 } 
            }}>
              <Box
                sx={{
                  width: { xs: 56, sm: 64 },
                  height: { xs: 56, sm: 64 },
                  borderRadius: '50%',
                  bgcolor: 'rgba(209, 213, 219, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2
                }}
              >
                <CreditCard size={24} color="#9CA3AF" />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  color: '#111827',
                  mb: 1
                }}
              >
                No bank account connected
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{
                  maxWidth: 240,
                  mx: 'auto',
                  mb: 2,
                  fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                  lineHeight: 1.5
                }}
              >
                Add a bank account to receive your payouts
              </Typography>
            </Box>
          )}

          {/* Button to manage bank accounts */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleOpenBankModal}
            startIcon={<Plus size={16} />}
            sx={{
              bgcolor: '#FF4775',
              color: 'white',
              borderRadius: 50,
              py: { xs: 1.25, sm: 1.5 },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              '&:hover': {
                bgcolor: '#E63E69'
              }
            }}
          >
            {bankAccounts.length > 0 ? 'Manage Bank Accounts' : 'Add Bank Account'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

// Enhanced Next Payout Card Component
const NextPayoutCard = ({ nextPayout, formatDate, formatCurrency }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Card 
      sx={{ 
        height: '100%',
        borderRadius: { xs: 3, sm: 4 },
        overflow: 'hidden',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.8)',
        bgcolor: 'white'
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ p: { xs: 3, sm: 4 }, pb: { xs: 2, sm: 3 } }}>
          {/* Header with icon and title */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: { xs: 3, sm: 4 },
            gap: 1.5
          }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: 'rgba(255, 71, 117, 0.1)',
                mr: 1.5,
                flexShrink: 0
              }}
            >
              <Calendar size={20} style={{ color: '#FF4775' }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.125rem', sm: '1.25rem' },
                color: '#111827'
              }}
            >
              Next Payout
            </Typography>
          </Box>
          
          {nextPayout ? (
            <>
              {/* Amount - Highlighted and prominent */}
              <Box 
                sx={{ 
                  mb: { xs: 3, sm: 4 },
                }}
              >
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 800, 
                    color: '#FF4775',
                    fontSize: { xs: '2rem', sm: '2.5rem' },
                    letterSpacing: '-0.02em'
                  }}
                >
                  {formatCurrency(nextPayout.amount)}
                </Typography>
              </Box>
              
              {/* Payment details with icons */}
              <Box sx={{ mb: { xs: 3, sm: 4 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  mb: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 1, sm: 0 }
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    minWidth: { sm: '50%' },
                    mb: { xs: 0.5, sm: 0 }
                  }}>
                    <Calendar size={16} style={{ color: 'rgba(0,0,0,0.5)', marginRight: 8, flexShrink: 0 }} />
                    <Typography 
                      variant="body2" 
                      color="textSecondary" 
                      sx={{ 
                        fontWeight: 600,
                        fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                      }}
                    >
                      Scheduled Date:
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 700,
                      fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                      ml: { xs: '24px', sm: 'auto' }
                    }}
                  >
                    {formatDate(nextPayout.scheduled_date)}
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  mb: 1,
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 1, sm: 0 }
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    minWidth: { sm: '50%' },
                    mb: { xs: 0.5, sm: 0 }
                  }}>
                    <Clock size={16} style={{ color: 'rgba(0,0,0,0.5)', marginRight: 8, flexShrink: 0 }} />
                    <Typography 
                      variant="body2" 
                      color="textSecondary" 
                      sx={{ 
                        fontWeight: 600,
                        fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                      }}
                    >
                      Expected Arrival:
                    </Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 700,
                      fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                      ml: { xs: '24px', sm: 'auto' }
                    }}
                  >
                    {formatDate(nextPayout.arrival_date)}
                  </Typography>
                </Box>
              </Box>
              
              {/* Status indicator */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: { xs: 'flex-start', sm: 'flex-end' }, 
                alignItems: 'center' 
              }}>
                <Typography 
                  variant="body2" 
                  color="textSecondary" 
                  sx={{ 
                    fontWeight: 600, 
                    mr: 1.5,
                    fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                  }}
                >
                  Status:
                </Typography>
                <Chip
                  label="scheduled"
                  size="small"
                  sx={{
                    bgcolor: 'rgba(58, 204, 225, 0.1)',
                    color: '#00B4D8',
                    fontWeight: 600,
                    px: 1,
                    borderRadius: '50px',
                    height: 28,
                    fontSize: { xs: '0.75rem', sm: '0.8125rem' }
                  }}
                />
              </Box>
            </>
          ) : (
            <Box 
              sx={{ 
                py: { xs: 3, sm: 4 }, 
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Box 
                sx={{ 
                  width: { xs: 60, sm: 70 }, 
                  height: { xs: 60, sm: 70 }, 
                  borderRadius: '50%', 
                  bgcolor: 'rgba(0, 0, 0, 0.03)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2
                }}
              >
                <Calendar size={28} color="rgba(0, 0, 0, 0.3)" />
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700,
                  mb: 1,
                  color: 'rgba(0, 0, 0, 0.75)',
                  fontSize: { xs: '1rem', sm: '1.125rem' }
                }}
              >
                No upcoming payouts scheduled
              </Typography>
              <Typography 
                variant="body2" 
                color="textSecondary" 
                sx={{ 
                  maxWidth: 300, 
                  mx: 'auto',
                  lineHeight: 1.6,
                  fontSize: { xs: '0.8125rem', sm: '0.875rem' }
                }}
              >
                Payouts are typically processed after your first billing cycle is complete
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// Enhanced Mobile Payout History Item - Clean and Compact Design
const MobilePayoutHistoryItem = ({ payout, formatDate, formatCurrency, getStatusChip, onDetailsClick }) => {
  return (
    <Box
      sx={{
        mb: 2,
        bgcolor: 'white',
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        }
      }}
    >
      <Box sx={{ p: 2.5 }}>
        {/* Top row with icon and download */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 1.5 
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5 
          }}>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: 'rgba(255, 71, 117, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <DollarSign size={18} color="#FF4775" />
            </Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                fontSize: '0.9rem',
                color: '#333'
              }}
            >
              {/* Could add payout ID here if available */}
              Payout #{payout.id?.slice(-4) || '0000'}
            </Typography>
          </Box>
          <IconButton
            onClick={onDetailsClick}
            size="small"
            sx={{
              bgcolor: 'rgba(255, 71, 117, 0.1)',
              p: 1,
              '&:hover': {
                bgcolor: 'rgba(255, 71, 117, 0.15)',
              }
            }}
          >
            <Eye size={16} color="#FF4775" />
          </IconButton>
        </Box>

        {/* Middle row with description */}
        <Box sx={{ mb: 1.5, ml: 6.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                fontSize: '0.85rem'
              }}
            >
              {payout.method === 'bank_account'
                ? `Bank •••• ${payout.bank_last4 || '****'}`
                : payout.method}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Calendar size={14} style={{ marginRight: 6, color: '#6B7280' }} />
            <Typography
              variant="caption"
              sx={{
                color: '#6B7280',
                fontSize: '0.75rem'
              }}
            >
              {formatDate(payout.created_at)}
            </Typography>
          </Box>
        </Box>

        {/* Bottom row with amount and status */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '1.1rem',
              color: '#00B4D8'
            }}
          >
            {formatCurrency(payout.amount)}
          </Typography>
          {getStatusChip(payout.status)}
        </Box>
      </Box>
    </Box>
  );
};

// Enhanced Background Graphics Component
const BackgroundGraphics = ({ isMobile }) => {
  return (
    <Box sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      zIndex: 0,
      pointerEvents: 'none',
      opacity: isMobile ? 0.5 : 1
    }}>
      {/* Enhanced gradient circles */}
      <Box sx={{
        position: 'absolute',
        top: '-5%',
        left: '-2%',
        width: { xs: '60%', md: '40%' },
        height: { xs: '60%', md: '40%' },
        background: 'radial-gradient(circle, rgba(255, 71, 117, 0.15) 0%, rgba(255, 71, 117, 0.08) 40%, rgba(255, 71, 117, 0) 70%)',
        borderRadius: '50%',
        filter: 'blur(50px)',
        animation: 'float 18s ease-in-out infinite alternate'
      }} />

      <Box sx={{
        position: 'absolute',
        bottom: '-8%',
        right: '-3%',
        width: { xs: '50%', md: '35%' },
        height: { xs: '50%', md: '35%' },
        background: 'radial-gradient(circle, rgba(0, 180, 216, 0.12) 0%, rgba(0, 180, 216, 0.05) 45%, rgba(0, 180, 216, 0) 70%)',
        borderRadius: '50%',
        filter: 'blur(50px)',
        animation: 'float 20s ease-in-out infinite alternate-reverse'
      }} />

      <Box sx={{
        position: 'absolute',
        top: '30%',
        right: '10%',
        width: { xs: '30%', md: '20%' },
        height: { xs: '30%', md: '20%' },
        background: 'radial-gradient(circle, rgba(58, 204, 225, 0.1) 0%, rgba(58, 204, 225, 0.04) 50%, rgba(58, 204, 225, 0) 70%)',
        borderRadius: '50%',
        filter: 'blur(35px)',
        animation: 'pulse 10s ease-in-out infinite'
      }} />
      
      {/* Decorative dot patterns (hidden on mobile) */}
      <Box sx={{
        display: { xs: 'none', md: 'block' },
        position: 'absolute',
        top: '15%',
        left: '12%',
        width: '180px',
        height: '180px',
        backgroundImage: 'radial-gradient(circle, rgba(255, 71, 117, 0.1) 1px, transparent 1px)',
        backgroundSize: '18px 18px',
        transform: 'rotate(15deg)',
        opacity: 0.5
      }} />
      
      <Box sx={{
        display: { xs: 'none', md: 'block' },
        position: 'absolute',
        bottom: '12%',
        right: '15%',
        width: '120px',
        height: '120px',
        backgroundImage: 'radial-gradient(circle, rgba(0, 180, 216, 0.1) 1px, transparent 1px)',
        backgroundSize: '12px 12px',
        transform: 'rotate(-10deg)',
        opacity: 0.4
      }} />
    </Box>
  );
};

const PayoutPage = () => {
  const { getAccessTokenSilently } = useAuth0();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // State
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [nextPayout, setNextPayout] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [defaultBankAccount, setDefaultBankAccount] = useState(null);
  const [isConnectAccountCreated, setIsConnectAccountCreated] = useState(false);
  
  // Modals state
  const [bankModalOpen, setBankModalOpen] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  
  // Effect to handle sidebar state on mobile/desktop
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);
  
  // Toggle sidebar
  const handleToggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };
  
  // Get auth token with proper audience and scope
  const getAuthToken = async () => {
    return await getAccessTokenSilently({
      audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      scope: 'openid profile email'
    });
  };
  
  // Load payout data
  useEffect(() => {
    const fetchPayoutData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const token = await getAuthToken();
        
        // Check if user has a Connect account
        const accountResponse = await payoutService.getAccount(token);
        
        setIsConnectAccountCreated(accountResponse.data.is_account_created);
        
        if (accountResponse.data.is_account_created) {
          // Fetch payout history
          const payoutResponse = await payoutService.getPayoutHistory(token);
          
          setPayoutHistory(payoutResponse.data.payouts || []);
          
          // Fetch next payout info
          const nextPayoutResponse = await payoutService.getNextPayout(token);
          
          setNextPayout(nextPayoutResponse.data);
          
          // Fetch all bank accounts
          const bankResponse = await payoutService.getBankAccounts(token);
          
          // Store all bank accounts in state (not just the default)
          setBankAccounts(bankResponse.data.bank_accounts || []);
          setDefaultBankAccount(bankResponse.data.default_account || null);
        }
      } catch (err) {
        console.error('Error fetching payout data', err);
        setError(err.friendlyMessage || 'Failed to load payout information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPayoutData();
  }, [getAccessTokenSilently]);
  
  const createConnectAccount = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = await getAuthToken();
      
      const response = await payoutService.createAccount({}, token);
      
      // Redirect to Stripe Connect Onboarding
      if (response.data && response.data.url) {
        window.location.href = response.data.url;
      } else {
        setError('No redirect URL received from server. Check server logs.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error creating Connect account:', err);
      
      if (err.response) {
        setError(`Server error: ${err.response.status} - ${JSON.stringify(err.response.data)}`);
      } else if (err.request) {
        setError('No response received from server. Check network connection or server logs.');
      } else {
        setError(`Error: ${err.message}`);
      }
      setLoading(false);
    }
  };
  
  const refreshPayoutData = async () => {
    setLoading(true);
    try {
      const token = await getAuthToken();
      
      // Fetch payout history
      const payoutResponse = await payoutService.getPayoutHistory(token);
      
      setPayoutHistory(payoutResponse.data.payouts || []);
      
      // Fetch next payout info
      const nextPayoutResponse = await payoutService.getNextPayout(token);
      
      setNextPayout(nextPayoutResponse.data);
      
      // Fetch updated bank accounts to get the latest default and any new accounts
      const bankResponse = await payoutService.getBankAccounts(token);
      
      // Update bank accounts state
      setBankAccounts(bankResponse.data.bank_accounts || []);
      setDefaultBankAccount(bankResponse.data.default_account || null);
      
      setError(null);
    } catch (err) {
      console.error('Error refreshing data:', err);
      setError(err.friendlyMessage || 'Failed to refresh data. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdateBankInfo = () => {
    setBankModalOpen(true);
  };
  
  // Enhanced status chip with improved visual design
  const getStatusChip = (status) => {
    const statusMap = {
      'paid': { 
        color: 'success',
        label: 'Paid', 
        icon: <CheckCircle size={14} />,
        sx: { 
          fontWeight: 600,
          color: '#10B981', 
          bgcolor: '#D1FAE5',
          px: 1.5,
          py: 1,
          height: 'auto',
          '& .MuiChip-label': { px: 0.5 },
          '& .MuiChip-icon': { color: '#10B981' }
        }
      },
      'pending': { 
        color: 'warning',
        label: 'Pending', 
        icon: <Clock size={14} />,
        sx: { 
          fontWeight: 600,
          color: '#F59E0B', 
          bgcolor: '#FEF3C7',
          px: 1.5,
          py: 1,
          height: 'auto',
          '& .MuiChip-label': { px: 0.5 },
          '& .MuiChip-icon': { color: '#F59E0B' }
        }
      },
      'in_transit': { 
        color: 'info',
        label: 'In Transit', 
        icon: <Clock size={14} />,
        sx: { 
          fontWeight: 600,
          color: '#00B4D8', 
          bgcolor: '#DBEAFE',
          px: 1.5,
          py: 1,
          height: 'auto',
          '& .MuiChip-label': { px: 0.5 },
          '& .MuiChip-icon': { color: '#00B4D8' }
        }
      },
      'failed': { 
        color: 'error',
        label: 'Failed', 
        icon: <AlertTriangle size={14} />,
        sx: { 
          fontWeight: 600,
          color: '#EF4444', 
          bgcolor: '#FEE2E2',
          px: 1.5,
          py: 1,
          height: 'auto',
          '& .MuiChip-label': { px: 0.5 },
          '& .MuiChip-icon': { color: '#EF4444' }
        }
      },
      'canceled': { 
        color: 'default',
        label: 'Canceled', 
        icon: <AlertTriangle size={14} />,
        sx: { 
          fontWeight: 600,
          color: '#6B7280', 
          bgcolor: '#F3F4F6',
          px: 1.5,
          py: 1,
          height: 'auto',
          '& .MuiChip-label': { px: 0.5 },
          '& .MuiChip-icon': { color: '#6B7280' }
        }
      }
    };
    
    const statusConfig = statusMap[status] || { 
      color: 'default', 
      label: status, 
      icon: null,
      sx: { 
        fontWeight: 600,
        color: '#6B7280', 
        bgcolor: '#F3F4F6',
        px: 1.5,
        py: 1,
        height: 'auto'
      }
    };
    
    return (
      <Chip 
        icon={statusConfig.icon}
        label={statusConfig.label} 
        color={statusConfig.color} 
        size="small" 
        sx={statusConfig.sx}
      />
    );
  };
  
  const handleOpenBankModal = () => {
    setBankModalOpen(true);
  };
  
  const handleCloseBankModal = () => {
    setBankModalOpen(false);
  };
  
  const handleOpenDetailsModal = (payout) => {
    setSelectedPayout(payout);
    setDetailsModalOpen(true);
  };
  
  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy');
    } catch (e) {
      return dateString;
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      bgcolor: '#FFF3F7',
      minHeight: '100vh',
      position: 'relative'
    }}>
      {/* Sidebar */}
      <Sidebar 
        isOpen={sidebarOpen} 
        onToggle={handleToggleSidebar}
      />
      
      {/* Mobile Header - simplified with title and refresh button */}
      <Box
        component="header"
        sx={{ 
          display: { xs: 'flex', md: 'none' },
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bgcolor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          zIndex: 1200,
          px: 2.5,
          py: 1.5,
          boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Typography 
          variant="h6" 
          component="div" 
          sx={{ 
            fontWeight: 700,
            color: '#1A1B25',
            fontSize: '1.25rem'
          }}
        >
          Payout Center
        </Typography>
        
        <IconButton
          onClick={refreshPayoutData}
          disabled={loading}
          sx={{ 
            color: '#FF4775',
            opacity: loading ? 0.5 : 1,
            bgcolor: 'rgba(255, 71, 117, 0.05)',
            '&:hover': {
              bgcolor: 'rgba(255, 71, 117, 0.1)',
            }
          }}
        >
          <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
        </IconButton>
      </Box>
      
      {/* Backdrop for mobile sidebar */}
      {isMobile && sidebarOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(2px)',
            zIndex: 1100,
            display: { xs: 'block', md: 'none' }
          }}
          onClick={handleToggleSidebar}
        />
      )}
      
      {/* Main Content */}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: { xs: 2.5, sm: 3, md: 4 },
          ml: { 
            xs: 0, 
            md: sidebarOpen ? '240px' : '80px' 
          },
          width: { 
            xs: '100%', 
            md: `calc(100% - ${sidebarOpen ? '240px' : '80px'})` 
          },
          transition: theme => theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          })
        }}
      >
        {/* Add top padding on mobile to account for the fixed header */}
        <Box sx={{ pt: { xs: 7, sm: 8, md: 1 }, pb: 4 }}>
          {/* Error Alert */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: { xs: 3, md: 4 }, 
                borderRadius: { xs: 2, md: 3 },
                position: 'relative',
                zIndex: 1,
                animation: 'fadeIn 0.3s ease-in-out',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.08)',
                fontWeight: 500,
                fontSize: { xs: '0.875rem', md: '0.95rem' },
                border: '1px solid rgba(239, 68, 68, 0.15)',
                '& .MuiAlert-icon': {
                  fontSize: { xs: '1.25rem', md: '1.5rem' }
                }
              }}
            >
              {error}
            </Alert>
          )}
          
          {/* Content Cards */}
          <Box 
            sx={{ 
              position: 'relative',
              minHeight: '80vh',
              maxWidth: '1600px',
              mx: 'auto',
              animation: 'fadeIn 0.5s ease-in-out'
            }}
          >
            {/* Background Graphics */}
            <BackgroundGraphics isMobile={isMobile} />
            
            {/* Loading State */}
            {loading && !payoutHistory.length ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '40vh',
                  flexDirection: 'column',
                  gap: 3,
                  my: 8
                }}
              >
                <Box 
                  sx={{ 
                    position: 'relative',
                    width: 60,
                    height: 60
                  }}
                >
                  <CircularProgress 
                    size={60} 
                    thickness={3}
                    sx={{ 
                      color: '#FF4775',
                      position: 'absolute',
                      top: 0,
                      left: 0
                    }} 
                  />
                  <Box 
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)'
                    }}
                  >
                    <DollarSign size={26} color="#FF4775" />
                  </Box>
                </Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    color: 'rgba(0,0,0,0.75)',
                    fontSize: { xs: '1rem', md: '1.25rem' }
                  }}
                >
                  Loading payment information...
                </Typography>
              </Box>
            ) : (
              <>
                {/* Set Up Payout Account Card or Main Content */}
                {!isConnectAccountCreated ? (
                  <Card 
                    sx={{ 
                      borderRadius: { xs: 3, md: 4 },
                      background: 'rgba(255, 255, 255, 0.95)',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.07)',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      border: '1px solid rgba(255, 255, 255, 0.8)',
                      position: 'relative',
                      zIndex: 1,
                      animation: 'slideUp 0.5s ease-out',
                      mb: 5,
                      mt: 2
                    }}
                  >
                    <CardContent sx={{ p: 0 }}>
                      <Box sx={{ 
                        textAlign: 'center', 
                        py: { xs: 6, md: 8 }, 
                        px: { xs: 3, sm: 6 },
                        position: 'relative'
                      }}>
                        {/* Decorative elements */}
                        <Box 
                          sx={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            right: 0, 
                            height: '100%', 
                            background: 'linear-gradient(135deg, rgba(255, 71, 117, 0.03), transparent 70%)',
                            zIndex: 0
                          }}
                        />
                        
                        <Box 
                          sx={{
                            position: 'relative',
                            zIndex: 1
                          }}
                        >
                          <Avatar
                            sx={{
                              width: { xs: 88, md: 112 },
                              height: { xs: 88, md: 112 },
                              bgcolor: 'rgba(255, 71, 117, 0.1)',
                              color: '#FF4775',
                              mb: { xs: 3, md: 4 },
                              mx: 'auto',
                              boxShadow: '0 12px 24px rgba(255, 71, 117, 0.15)',
                              transform: 'translateY(0)',
                              transition: 'all 0.4s ease',
                              border: '6px solid rgba(255, 71, 117, 0.05)',
                              '&:hover': {
                                transform: 'translateY(-8px)',
                                boxShadow: '0 18px 32px rgba(255, 71, 117, 0.25)',
                              }
                            }}
                          >
                            <DollarSign size={isMobile ? 42 : 52} />
                          </Avatar>
                          
                          <Typography 
                            variant="h4" 
                            gutterBottom 
                            sx={{ 
                              fontWeight: 800,
                              mb: 2,
                              letterSpacing: '-0.02em',
                              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
                            }}
                          >
                            Set Up Your Payout Account
                          </Typography>
                          
                          <Typography 
                            variant="body1" 
                            color="textSecondary" 
                            sx={{ 
                              mb: { xs: 4, md: 5 }, 
                              maxWidth: 650, 
                              mx: 'auto',
                              lineHeight: 1.7,
                              fontSize: { xs: '0.9375rem', md: '1.05rem' }
                            }}
                          >
                            To receive payouts, you'll need to connect a bank account to your CircledIn account.
                            This is a one-time setup process that uses Stripe to securely handle your banking information.
                          </Typography>
                          
                          <Button 
                            variant="contained" 
                            color="primary"
                            onClick={createConnectAccount}
                            disabled={loading}
                            sx={{
                              px: { xs: 4, md: 5 },
                              py: { xs: 1.25, md: 1.5 },
                              borderRadius: 50,
                              textTransform: 'none',
                              fontWeight: 700,
                              fontSize: { xs: '1rem', md: '1.1rem' },
                              background: 'linear-gradient(135deg, #FF4775 0%, #FF5E8A 100%)',
                              boxShadow: '0 10px 25px rgba(255, 71, 117, 0.35)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #FF4775 0%, #FF5E8A 100%)',
                                boxShadow: '0 14px 32px rgba(255, 71, 117, 0.45)',
                                transform: 'translateY(-3px)'
                              },
                              '&:active': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 8px 20px rgba(255, 71, 117, 0.4)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            {loading ? (
                              <CircularProgress size={24} sx={{ color: 'white' }} />
                            ) : (
                              'Set Up Payout Account'
                            )}
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {/* Next Payout & Payment Method Cards */}
                    <Grid container spacing={{ xs: 5, md: 5 }} sx={{ mb: { xs: 5, md: 5 }, position: 'relative', zIndex: 1 }}>
                      {/* Next Payout Card */}
                      <Grid item xs={12} md={6}>
                        <NextPayoutCard 
                          nextPayout={nextPayout} 
                          formatDate={formatDate} 
                          formatCurrency={formatCurrency} 
                        />
                      </Grid>
                      
                      {/* Payment Method Card - Now displays all bank accounts */}
                      <Grid item xs={12} md={6}>
                        <PaymentMethodCard 
                          bankAccounts={bankAccounts}
                          defaultBankAccount={defaultBankAccount} 
                          formatDate={formatDate} 
                          handleOpenBankModal={handleOpenBankModal} 
                        />
                      </Grid>
                    </Grid>
                    
                    {/* Payout History */}
                    <Box sx={{ mb: { xs: 5, md: 5 }, position: 'relative', zIndex: 1 }}>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          mb: { xs: 3, md: 3 },
                          justifyContent: 'space-between'
                        }}
                      >
                        <Typography 
                          variant="h5" 
                          sx={{ 
                            fontWeight: 800,
                            color: '#1A1B25',
                            mr: 2,
                            fontSize: { xs: '1.25rem', md: '1.5rem' }
                          }}
                        >
                          Payout History
                        </Typography>
                        
                        <Chip 
                          label={`${payoutHistory.length} ${payoutHistory.length === 1 ? 'Payout' : 'Payouts'}`}
                          sx={{
                            borderRadius: 50,
                            fontWeight: 700,
                            fontSize: { xs: '0.75rem', md: '0.8125rem' },
                            background: 'rgba(255, 71, 117, 0.1)',
                            color: '#FF4775',
                            border: '1px solid rgba(255, 71, 117, 0.2)'
                          }}
                        />
                      </Box>
                      
                      {payoutHistory.length > 0 ? (
                        <>
                          {/* Desktop Table View */}
                          <TableContainer 
                            component={Paper} 
                            sx={{ 
                              display: { xs: 'none', md: 'block' },
                              borderRadius: 4,
                              background: 'rgba(255, 255, 255, 0.95)',
                              backdropFilter: 'blur(10px)',
                              WebkitBackdropFilter: 'blur(10px)',
                              boxShadow: '0 15px 35px rgba(0, 0, 0, 0.05)',
                              overflow: 'hidden',
                              border: '1px solid rgba(255, 255, 255, 0.8)',
                              animation: 'fadeIn 0.5s ease-in-out',
                            }}
                          >
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell 
                                    sx={{ 
                                      fontWeight: 800, 
                                      fontSize: '0.95rem',
                                      borderBottom: '2px solid',
                                      borderColor: alpha('#1A1B25', 0.08),
                                      py: 2.5,
                                      px: 3,
                                      color: alpha('#1A1B25', 0.85)
                                    }}
                                  >
                                    Date
                                  </TableCell>
                                  <TableCell 
                                    sx={{ 
                                      fontWeight: 800, 
                                      fontSize: '0.95rem',
                                      borderBottom: '2px solid',
                                      borderColor: alpha('#1A1B25', 0.08),
                                      py: 2.5,
                                      px: 3,
                                      color: alpha('#1A1B25', 0.85)
                                    }}
                                  >
                                    Amount
                                  </TableCell>
                                  <TableCell 
                                    sx={{ 
                                      fontWeight: 800, 
                                      fontSize: '0.95rem',
                                      borderBottom: '2px solid',
                                      borderColor: alpha('#1A1B25', 0.08),
                                      py: 2.5,
                                      px: 3,
                                      color: alpha('#1A1B25', 0.85)
                                    }}
                                  >
                                    Status
                                  </TableCell>
                                  <TableCell 
                                    sx={{ 
                                      fontWeight: 800, 
                                      fontSize: '0.95rem',
                                      borderBottom: '2px solid',
                                      borderColor: alpha('#1A1B25', 0.08),
                                      py: 2.5,
                                      px: 3,
                                      color: alpha('#1A1B25', 0.85)
                                    }}
                                  >
                                    Method
                                  </TableCell>
                                  <TableCell 
                                    sx={{ 
                                      fontWeight: 800, 
                                      fontSize: '0.95rem',
                                      borderBottom: '2px solid',
                                      borderColor: alpha('#1A1B25', 0.08),
                                      py: 2.5,
                                      px: 3,
                                      color: alpha('#1A1B25', 0.85)
                                    }}
                                  >
                                    Actions
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {payoutHistory.map((payout, index) => (
                                  <TableRow 
                                    key={payout.id} 
                                    hover 
                                    sx={{ 
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease',
                                      '&:hover': {
                                        bgcolor: 'rgba(255, 71, 117, 0.02)',
                                      },
                                      animation: `fadeIn 0.5s ease forwards ${index * 0.1}s`
                                    }}
                                    onClick={() => handleOpenDetailsModal(payout)}
                                  >
                                    <TableCell sx={{ py: 2.5, px: 3 }}>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          fontWeight: 600,
                                          color: alpha('#1A1B25', 0.85)
                                        }}
                                      >
                                        {formatDate(payout.created_at)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 2.5, px: 3 }}>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          fontWeight: 700,
                                          color: '#00B4D8'
                                        }}
                                      >
                                        {formatCurrency(payout.amount)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 2.5, px: 3 }}>
                                      {getStatusChip(payout.status)}
                                    </TableCell>
                                    <TableCell sx={{ py: 2.5, px: 3 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <CreditCard size={16} style={{ marginRight: 8, color: '#6B7280' }} />
                                        <Typography 
                                          variant="body2"
                                          sx={{ 
                                            fontWeight: 600,
                                            color: alpha('#1A1B25', 0.75)
                                          }}
                                        >
                                          {payout.method === 'bank_account'
                                            ? `Bank •••• ${payout.bank_last4 || '****'}`
                                            : payout.method}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell sx={{ py: 2.5, px: 3 }}>
                                      <Button
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        endIcon={<ArrowRight size={16} />}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenDetailsModal(payout);
                                        }}
                                        sx={{ 
                                          textTransform: 'none',
                                          fontWeight: 700,
                                          borderRadius: 50,
                                          px: 2,
                                          py: 0.7,
                                          borderColor: alpha('#FF4775', 0.25),
                                          color: '#FF4775',
                                          '&:hover': {
                                            borderColor: alpha('#FF4775', 0.5),
                                            background: alpha('#FF4775', 0.05)
                                          }
                                        }}
                                      >
                                        View Details
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>

                          {/* Mobile Card View */}
                          <Box 
                            sx={{ 
                              display: { xs: 'block', md: 'none' }
                            }}
                          >
                            {payoutHistory.map((payout, index) => (
                              <MobilePayoutHistoryItem 
                                key={payout.id || index}
                                payout={payout}
                                formatDate={formatDate}
                                formatCurrency={formatCurrency}
                                getStatusChip={getStatusChip}
                                onDetailsClick={() => handleOpenDetailsModal(payout)}
                              />
                            ))}
                          </Box>
                        </>
                      ) : (
                        <NoPayoutsPlaceholder onUpdateBankInfo={handleUpdateBankInfo} />
                      )}
                    </Box>
                    
                    {/* Help Section */}
                    <Card
                      sx={{
                        borderRadius: { xs: 3, md: 4 },
                        background: 'linear-gradient(135deg, rgba(0, 180, 216, 0.05) 0%, rgba(0, 180, 216, 0.1) 100%)',
                        backdropFilter: 'blur(5px)',
                        WebkitBackdropFilter: 'blur(5px)',
                        border: '1px solid rgba(0, 180, 216, 0.1)',
                        animation: 'fadeIn 0.5s ease-in-out',
                        position: 'relative',
                        zIndex: 1,
                        overflow: 'hidden',
                        boxShadow: 'none',
                        mb: { xs: 2, md: 0 }
                      }}
                    >
                      <CardContent sx={{ p: { xs: 3, md: 4 }, position: 'relative' }}>
                        <Box 
                          sx={{ 
                            display: 'flex', 
                            alignItems: { xs: 'flex-start', md: 'center' },
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: { xs: 2, md: 0 }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                            <Avatar
                              sx={{
                                bgcolor: 'rgba(0, 180, 216, 0.1)',
                                color: '#00B4D8',
                                mr: 2,
                                width: { xs: 40, md: 44 },
                                height: { xs: 40, md: 44 },
                                border: '1px solid rgba(0, 180, 216, 0.3)'
                              }}
                            >
                              <Info size={22} />
                            </Avatar>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                fontWeight: 700,
                                color: '#00B4D8',
                                fontSize: { xs: '1rem', md: '1.125rem' }
                              }}
                            >
                              Need Help?
                            </Typography>
                          </Box>
                          
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              mx: { xs: 0, md: 3 },
                              flex: 1,
                              lineHeight: 1.6,
                              color: alpha('#1A1B25', 0.7),
                              fontSize: { xs: '0.875rem', md: '0.95rem' },
                              mb: { xs: 1, md: 0 }
                            }}
                          >
                            Visit our help center to learn more about payouts, billing cycles, and how to manage your banking information.
                          </Typography>
                          
                          <Button
                            variant="outlined"
                            endIcon={<ChevronRight size={16} />}
                            href="https://circledin.zendesk.com/hc/en-us"
                            target="_blank"
                            sx={{
                              borderRadius: 50,
                              textTransform: 'none',
                              fontWeight: 700,
                              whiteSpace: 'nowrap',
                              color: '#00B4D8',
                              borderColor: alpha('#00B4D8', 0.3),
                              borderWidth: 2,
                              px: { xs: 3, md: 4 },
                              py: 1,
                              fontSize: { xs: '0.875rem', md: '0.9375rem' },
                              alignSelf: { xs: 'flex-end', md: 'center' },
                              '&:hover': {
                                borderWidth: 2,
                                borderColor: '#00B4D8',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 5px 15px rgba(0, 180, 216, 0.15)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            Help Center
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
      
      {/* Bank Account Modal - Updated to use payoutService */}
      <BankAccountModal
        open={bankModalOpen}
        onClose={handleCloseBankModal}
        existingAccounts={bankAccounts}
        defaultAccount={defaultBankAccount}
        refreshData={refreshPayoutData}
        getAuthToken={getAuthToken}
        payoutService={payoutService}
      />
      
      {/* Payout Details Modal */}
      {selectedPayout && (
        <PayoutDetailsModal
          open={detailsModalOpen}
          onClose={handleCloseDetailsModal}
          payout={selectedPayout}
        />
      )}
      
      {/* Add animation keyframes */}
      <style jsx global>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
          100% { transform: translateY(0px); }
        }
        
        @keyframes pulse {
          0% { opacity: 0.6; transform: scale(0.95); }
          50% { opacity: 1; transform: scale(1.05); }
          100% { opacity: 0.6; transform: scale(0.95); }
        }
        
        @keyframes shine {
          0% { left: -100%; opacity: 0; }
          20% { opacity: 0.5; }
          80% { opacity: 0.5; }
          100% { left: 100%; opacity: 0; }
        }

        .animate-spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default PayoutPage;